import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';

import { AgentSummary, WorkflowsQueriesService } from 'src/shared/services/workflows-queries.service';

import { TranslationPipe } from 'src/tidy-ui-components/pipes/translation.pipe';

import { AiLoaderComponent } from 'src/shared/components/ai-loader/ai-loader.component';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

@Component({
  selector: 'automations-preview-button',
  templateUrl: './automations-preview-button.component.html',
  styleUrls: ['./automations-preview-button.component.scss'],
  standalone: true,
  // TidyUiComponentsModule imports TranslationPipe without re-exporting it. Without
  // this explicit import the template's `| translate` resolves to undefined in
  // production builds and the dropdown throws instead of rendering (Trello 18726).
  imports: [CommonModule, TidyUiComponentsModule, AiLoaderComponent, TranslationPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class AutomationsPreviewButtonComponent implements OnInit {

  private elementRef = inject(ElementRef);
  private navCtrl = inject(CustomNavController);
  private workflowsQueries = inject(WorkflowsQueriesService);

  // The workflow tag this page maps to (e.g. 'schedule'). Falls back to
  // sectionName (lowercased) for legacy usages.
  @Input() tag = '';
  @Input() sectionName = '';

  agents: AgentSummary[] = [];
  buttonText = '';
  loaded = false;
  isOpen = false;

  private cdr = inject(ChangeDetectorRef);

  async ngOnInit(): Promise<void> {
    const tag = (this.tag || this.sectionName || '').toLowerCase();
    if (!tag) {
      this.loaded = true;
      return;
    }
    try {
      const agents = await this.workflowsQueries.getAgentsForTag(tag);
      this.agents = [...agents].sort((a, b) => Number(b.enabled) - Number(a.enabled));
    } catch (error) {
      this.agents = [];
    }
    const count = this.agents.length;
    this.buttonText = `${count} Agent${count === 1 ? '' : 's'}`;
    this.loaded = true;
    this.cdr.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
  }

  goToAutomation(agent: AgentSummary): void {
    this.isOpen = false;
    // One-shot deep-link keys consumed by UnifiedAgentPage on entry — both
    // when it's freshly created (ngOnInit restore) and when it's revived from
    // the nav stack (NavigationEnd refresh). The regular unified_agent_* keys
    // can't be used: the page overwrites them on every selection.
    if (agent.triggerKey) {
      localStorage.setItem('unified_agent_deeplink_trigger_key', agent.triggerKey);
      localStorage.setItem('unified_agent_deeplink_workflow_id', String(agent.id));
    }
    this.navCtrl.navigateForward('agent');
  }
}
